import { brandSubdomainBase, getRouterBasename } from "../config";

const RESERVED_LABELS = new Set([
  "www",
  "api",
  "admin",
  "app",
  "mail",
  "cdn",
  "static",
  "assets",
  "staging",
  "dev",
  "test",
]);

function hostnameOnly(host) {
  return String(host || "")
    .toLowerCase()
    .split(":")[0]
    .replace(/\.$/, "");
}

function isClubSubdomainLabel(label) {
  const sub = String(label || "").toLowerCase();
  return Boolean(sub) && !sub.includes(".") && !RESERVED_LABELS.has(sub);
}

/**
 * Club subdomains ({name}.smartttclub.com) are not in DNS. Public join/login
 * links must use the apex (or OVH dev) plus /join/:code.
 */
export function canonicalFrontendOrigin({
  host,
  protocol = "https:",
  subdomainBase = brandSubdomainBase,
} = {}) {
  const rawHost = String(host || "").trim();
  const hostname = hostnameOnly(rawHost);
  const base = String(subdomainBase || "smartttclub.com").toLowerCase();
  const proto = String(protocol || "https:").endsWith(":")
    ? protocol
    : `${protocol}:`;

  if (!hostname) return `https://${base}`;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    const withPort = rawHost.includes(":") ? rawHost : hostname;
    return `${proto}//${withPort}`;
  }
  if (hostname.includes("googleapis.com") || hostname.includes("cloudfront.net")) {
    return `https://${base}`;
  }
  if (hostname === base || hostname === `www.${base}`) {
    return `https://${base}`;
  }
  if (hostname.endsWith(`.${base}`)) {
    const sub = hostname.slice(0, -`.${base}`.length);
    if (isClubSubdomainLabel(sub)) {
      return `https://${base}`;
    }
  }
  return `${proto}//${hostname}`;
}

/**
 * Origin for copy/share links. Always apex or OVH — never {club}.smartttclub.com.
 */
export function publicShareOrigin({
  host,
  protocol = "https:",
  subdomainBase = brandSubdomainBase,
} = {}) {
  const hostname = hostnameOnly(host);
  const base = String(subdomainBase || "smartttclub.com").toLowerCase();

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return canonicalFrontendOrigin({ host, protocol, subdomainBase });
  }
  if (
    hostname === "193.70.75.208" ||
    hostname === "dev.smartttclub.com" ||
    hostname.endsWith(".dev.smartttclub.com") ||
    base === "dev.smartttclub.com"
  ) {
    return "https://dev.smartttclub.com";
  }
  return "https://smartttclub.com";
}

/**
 * Rewrite leftover {club}.smartttclub.com/join links to the apex path.
 * https://eschabol.smartttclub.com/join → https://smartttclub.com/join/eschabol
 */
export function rewriteLegacyClubHostJoinUrl(url) {
  if (!url) return url;
  try {
    const u = new URL(String(url));
    const host = hostnameOnly(u.hostname);
    let apex = null;
    let label = null;

    const prod = host.match(/^([a-z0-9-]+)\.smartttclub\.com$/);
    if (prod && isClubSubdomainLabel(prod[1])) {
      apex = "https://smartttclub.com";
      label = prod[1];
    }
    const dev = host.match(/^([a-z0-9-]+)\.dev\.smartttclub\.com$/);
    if (!apex && dev && isClubSubdomainLabel(dev[1])) {
      apex = "https://dev.smartttclub.com";
      label = dev[1];
    }
    if (!apex) return String(url);

    const parts = u.pathname.replace(/\/+$/, "").split("/").filter(Boolean);
    const joinIdx = parts.findIndex((p) => p.toLowerCase() === "join");
    const pathCode = joinIdx >= 0 ? parts[joinIdx + 1] : null;
    const code = pathCode || label;
    return `${apex}/join/${encodeURIComponent(code)}`;
  } catch {
    return String(url);
  }
}

function stripHostToJoinCode(raw) {
  const value = String(raw || "").trim();
  if (!value) return null;
  try {
    if (/^https?:\/\//i.test(value)) {
      const rewritten = rewriteLegacyClubHostJoinUrl(value);
      const u = new URL(rewritten);
      const parts = u.pathname.replace(/\/+$/, "").split("/").filter(Boolean);
      const joinIdx = parts.findIndex((p) => p.toLowerCase() === "join");
      if (joinIdx >= 0 && parts[joinIdx + 1]) return decodeURIComponent(parts[joinIdx + 1]);
    }
  } catch {
    /* fall through */
  }
  const hostOnly = value.replace(/^https?:\/\//i, "").split("/")[0];
  const prod = hostOnly.match(/^([a-z0-9-]+)\.smartttclub\.com$/i);
  if (prod && isClubSubdomainLabel(prod[1])) return prod[1].toLowerCase();
  const dev = hostOnly.match(/^([a-z0-9-]+)\.dev\.smartttclub\.com$/i);
  if (dev && isClubSubdomainLabel(dev[1])) return dev[1].toLowerCase();
  return value;
}

export function clubJoinPathCode(brand, extras = {}) {
  const raw =
    extras.club_subdomain ||
    brand?.club_subdomain ||
    extras.club_code ||
    brand?.club_code ||
    extras.club_id ||
    brand?.club_id ||
    null;
  return stripHostToJoinCode(raw);
}

function shareOriginFromExtras(extras = {}) {
  if (extras.origin) {
    try {
      const u = new URL(String(extras.origin));
      return publicShareOrigin({
        host: u.host,
        protocol: u.protocol,
      });
    } catch {
      /* fall through */
    }
  }
  if (typeof window !== "undefined" && window.location) {
    return publicShareOrigin({
      host: window.location.host,
      protocol: window.location.protocol,
    });
  }
  const base = String(brandSubdomainBase || "smartttclub.com").toLowerCase();
  return publicShareOrigin({ host: base });
}

/**
 * Shareable player join URL that resolves in DNS.
 * Example: https://smartttclub.com/join/eschabol
 */
export function getClubJoinPublicUrl(brand, extras = {}) {
  const code = clubJoinPathCode(brand, extras);
  if (!code) return "";

  const origin = shareOriginFromExtras(extras).replace(/\/$/, "");

  const host =
    typeof window !== "undefined" ? String(window.location?.hostname || "") : "";
  const basename = String(
    extras.basename != null
      ? extras.basename
      : host === "storage.googleapis.com"
        ? getRouterBasename()
        : ""
  ).replace(/\/$/, "");

  const path = `${basename}/join/${encodeURIComponent(code)}`.replace(/\/+/g, "/");
  return rewriteLegacyClubHostJoinUrl(`${origin}${path}`);
}
